import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { DocumentExtractionJob } from "./documentExtractionJobs";
import { isJobInReview } from "./documentExtractionJobs";

export function isOnJobContextPage(
  pathname: string,
  search: string,
  job: DocumentExtractionJob
): boolean {
  if (job.contextType === "library") {
    return pathname === "/document-library" || pathname.startsWith("/document-library/");
  }
  if (job.contextType === "asset") {
    if (job.contextId) return pathname === `/assets/${job.contextId}`;
    return pathname.startsWith("/assets");
  }
  if (job.contextType === "location") {
    if (!job.contextId) return pathname === "/locationmap";
    const params = new URLSearchParams(search);
    return pathname === "/locationmap" && params.get("locationId") === job.contextId;
  }
  return false;
}

export function jobMatchesUploadContext(
  job: DocumentExtractionJob,
  context: { type: DocumentExtractionJob["contextType"]; id?: string }
): boolean {
  if (job.contextType !== context.type) return false;
  if (context.id && job.contextId && context.id !== job.contextId) return false;
  return true;
}

export function useExtractionReviewRouteOpener(
  onReviewDocument: (documentId: string) => void
): void {
  const location = useLocation();
  const navigate = useNavigate();
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const documentId = params.get("reviewDocumentId");
    if (!documentId) {
      handledRef.current = null;
      return;
    }
    if (handledRef.current === documentId) return;

    handledRef.current = documentId;
    onReviewDocument(documentId);

    params.delete("reviewDocumentId");
    const nextSearch = params.toString();
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate, onReviewDocument]);
}

export function jobReadyForReview(
  before: DocumentExtractionJob,
  after: DocumentExtractionJob
): boolean {
  return !isJobInReview(before) && isJobInReview(after);
}
